import i18n from 'i18n';

import { PublicUtilityType } from 'models';
import Yup, { requiredIntegerInRange } from 'utils/yup';

import {
  MAX_APPOINTMENT_MINUTES,
  MAX_DATA_SYNC_INTERVAL_MINUTES,
  MAX_PHOTO_COUNT,
  MAX_RETENTION_DAYS,
  MIN_DATA_SYNC_INTERVAL_MINUTES,
  MIN_HEALTH_AND_SAFETY_RETENTION_DAYS,
} from './fieldDeviceSettingsConfig';

const rangeField = () =>
  Yup.object().shape({
    min: requiredIntegerInRange(0, MAX_PHOTO_COUNT).test(
      'notAboveMax',
      function notAboveMax(value) {
        const { max } = this.parent as { max?: number };

        return (
          value === undefined ||
          max === undefined ||
          value <= max ||
          this.createError({ message: i18n.t('ERRORS.NUM_FIELD_MAX_LENGTH', { max }) })
        );
      }
    ),
    max: requiredIntegerInRange(1, MAX_PHOTO_COUNT).test(
      'notBelowMin',
      function notBelowMin(value) {
        const { min } = this.parent as { min?: number };

        return (
          value === undefined ||
          min === undefined ||
          value >= min ||
          this.createError({ message: i18n.t('ERRORS.NUM_FIELD_MIN_LENGTH', { min }) })
        );
      }
    ),
  });

export const utilitySchema = Yup.object().shape({
  task: Yup.object().shape({
    deleteAfterReadingDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
    deleteAfterClosingDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
  }),
  gps: Yup.object().shape({
    required: Yup.boolean(),
    coordinateValidityInMinutes: requiredIntegerInRange(1, 1440),
  }),
  readingConfirmField: Yup.object().shape({
    enabled: Yup.boolean(),
  }),
  photo: Yup.object().shape({
    deleteAfterDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
    minQualityScore: requiredIntegerInRange(0, 100),
    retryCount: requiredIntegerInRange(0, 10),
    resolution: Yup.object(),
  }),
});

export const validationSchema = Yup.object().shape({
  message: Yup.object().shape({
    deleteAfterDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
  }),
  maintenanceMenu: Yup.object().shape({
    enabled: Yup.boolean(),
  }),
  gpsTracking: Yup.object().shape({
    enabled: Yup.boolean(),
  }),
  synchronization: Yup.object().shape({
    periodicSynchronizationRepeatInMinutes: requiredIntegerInRange(
      MIN_DATA_SYNC_INTERVAL_MINUTES,
      MAX_DATA_SYNC_INTERVAL_MINUTES
    ),
  }),
  utilityTypes: Yup.object().shape({
    [PublicUtilityType.ELECTRICITY]: utilitySchema,
    [PublicUtilityType.GAS]: utilitySchema,
  }),
  read: Yup.object().shape({
    meterPhotoCount: rangeField(),
    devicePhotoCount: rangeField(),
    keepWithdrawnTaskInDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
  }),
  check: Yup.object().shape({
    meterPhotoCount: rangeField(),
    devicePhotoCount: rangeField(),
    documentPhotoCount: rangeField(),
    keepCompletedTaskInDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
    keepWithdrawnTaskInDays: requiredIntegerInRange(1, MAX_RETENTION_DAYS),
  }),
  healthAndSafety: Yup.object().shape({
    photoCount: rangeField(),
    keepHealthAndSafetiesInDays: requiredIntegerInRange(
      MIN_HEALTH_AND_SAFETY_RETENTION_DAYS,
      MAX_RETENTION_DAYS
    ),
  }),
  appointment: Yup.object().shape({
    upcomingAppointmentsTimeframeInMinutes: requiredIntegerInRange(1, MAX_APPOINTMENT_MINUTES),
    upcomingAppointmentsCheckPeriodInMinutes: requiredIntegerInRange(1, MAX_APPOINTMENT_MINUTES),
  }),
});
